import { useEffect, useCallback, useRef } from 'react';
import { Keyboard, ScrollView, View } from 'react-native';
import { useMutation, useQuery } from '@apollo/client/react';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
import { Feature } from '@fytness/shared';
import { Text } from '@/components/ui/text';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { LayoutBreakpoints } from '@/constants/LayoutBreakpoints';
import { useIsWidthUp } from '~/global/hooks/useIsWidthUp';
import { useHasFeature } from '~/purchases/hooks/useHasFeature';
import { usePaywall } from '~/purchases/hooks/usePaywall';
import {
  ActivityLevel,
  CurrentUserDocument,
  FitnessGoal,
  Gender,
  UpdateCurrentUserDocument,
} from '~/graphql/generated/graphql';
import { FullHeightWrapper } from '~/layout/components/full-height-wrapper/FullHeightWrapper';
import { Icon } from '~/global/components/icon/Icon';
import { useRouter } from 'expo-router';
import { HealthConnectCard } from '~/health/components/health-connect-card/HealthConnectCard';
import { MeasurementSourceBadge } from '~/health/components/measurement-source-badge/MeasurementSourceBadge';
import { useBodyMeasurementImport } from '~/health/hooks/useBodyMeasurementImport';
import { BmiCalculator } from '~/health/lib/body-measurement/bmi-calculator';
import { usePowerSync } from '~/powersync/contexts/power-sync/PowerSync';
import { useLivePowerSyncQuery } from '~/powersync/hooks/useLivePowerSyncQuery';
import { db } from '@/lib/local-api';
import { RandomUtils } from '@fytness/shared';
import { ProfileAvatarSection } from './ProfileAvatarSection';
import { GenderSelector } from './GenderSelector';
import { FitnessGoalSelector } from './FitnessGoalSelector';
import { ActivityLevelSelector } from './ActivityLevelSelector';
import { DangerZoneCard } from './DangerZoneCard';

interface ProfileFormData {
  firstName: string;
  lastName: string;
  weight: string;
  height: string;
  calorieGoal: string;
  gender: Gender | null;
  fitnessGoal: FitnessGoal | null;
  activityLevel: ActivityLevel | null;
}

export const ProfileScreen = () => {
  const { t } = useTranslation('user');
  const router = useRouter();
  const isDesktop = useIsWidthUp(LayoutBreakpoints.desktop);
  const canMacroTargets = useHasFeature(Feature.MACRO_TARGETS);
  const { openPaywall } = usePaywall();

  const { data: userData } = useQuery(CurrentUserDocument);
  const currentUser = userData?.currentUser;

  const [updateUser, { loading: updating }] = useMutation(
    UpdateCurrentUserDocument,
  );

  // usePowerSync()'s kysely type resolves to `never` codebase-wide (see
  // useRecordedSessionImport); the runtime value is the real query builder.
  const powerSync = usePowerSync();

  // Opportunistically import body measurements from the health store while the
  // profile is open / on foreground, so the current weight below stays fresh.
  useBodyMeasurementImport();

  // Current weight + composition now live in the synced BodyMeasurement table,
  // not on User. The latest reading of any source is the current weight.
  const { data: latestMeasurement } = useLivePowerSyncQuery(
    currentUser ? db.BodyMeasurement.queries.latest(currentUser.id) : null,
    [currentUser?.id],
    { single: true },
  );

  const {
    control,
    handleSubmit,
    formState: { errors, isDirty, dirtyFields },
    reset,
    setValue,
    getValues,
  } = useForm<ProfileFormData>({
    defaultValues: {
      firstName: '',
      lastName: '',
      weight: '',
      height: '',
      calorieGoal: '',
      gender: null,
      fitnessGoal: null,
      activityLevel: null,
    },
  });

  useEffect(() => {
    if (currentUser) {
      reset({
        firstName: currentUser.firstName || '',
        lastName: currentUser.lastName || '',
        // Weight is seeded separately from the latest BodyMeasurement (below),
        // so it isn't clobbered when this resets on other User field changes.
        weight: '',
        height: currentUser.height != null ? String(currentUser.height) : '',
        calorieGoal:
          currentUser.calorieGoal != null
            ? String(currentUser.calorieGoal)
            : '',
        gender: currentUser.gender ?? null,
        fitnessGoal: currentUser.fitnessGoal ?? null,
        activityLevel: currentUser.activityLevel ?? null,
      });
    }
  }, [currentUser, reset]);

  // Seed the weight input whenever a *new* latest measurement arrives (keyed by its
  // id), so the field reflects the most recent reading even after one is logged/
  // imported while the profile stays mounted. Keying on the id (a ref, never a
  // `dirtyFields` dependency) is deliberate: depending on form state here while
  // calling `setValue` re-triggers the effect on the resulting re-render → an
  // infinite update loop. `setValue` doesn't change the id, so this can't loop.
  const seededMeasurementId = useRef<string | null>(null);
  useEffect(() => {
    const id = latestMeasurement?.id;
    if (
      id &&
      id !== seededMeasurementId.current &&
      latestMeasurement?.weight != null
    ) {
      seededMeasurementId.current = id;
      setValue('weight', String(latestMeasurement.weight));
    }
  }, [latestMeasurement?.id, latestMeasurement?.weight, setValue]);

  // A manual weight edit is re-homed as a MANUAL BodyMeasurement (append), not a
  // User.weight write. Only fires when the weight field was actually changed.
  const persistWeight = useCallback(
    async (weightValue: string) => {
      if (!currentUser || !dirtyFields.weight || !weightValue) return;
      const weight = parseFloat(weightValue);
      if (Number.isNaN(weight)) return;

      await db.BodyMeasurement.mutations.create(powerSync, {
        id: RandomUtils.uuid(),
        source: 'MANUAL',
        externalId: null,
        measuredAt: new Date().toISOString(),
        weight,
        bodyFatPercent: null,
        leanMass: null,
        userId: currentUser.id,
      });
    },
    [currentUser, dirtyFields.weight, powerSync],
  );

  const onSubmit = async (data: ProfileFormData) => {
    try {
      await updateUser({
        variables: {
          input: {
            firstName: data.firstName,
            lastName: data.lastName,
            height: data.height ? parseFloat(data.height) : null,
            calorieGoal: data.calorieGoal
              ? parseInt(data.calorieGoal, 10)
              : null,
            gender: data.gender,
            fitnessGoal: data.fitnessGoal,
            activityLevel: data.activityLevel,
          },
        },
      });
      await persistWeight(data.weight);
      reset(data);
    } catch (error) {
      console.error('Failed to update profile:', error);
    }
  };

  const handleSelectionChange = useCallback(
    async (
      field: 'gender' | 'fitnessGoal' | 'activityLevel',
      value: Gender | FitnessGoal | ActivityLevel,
    ) => {
      setValue(field, value as any, { shouldDirty: true });
      const data = getValues();
      const updated = { ...data, [field]: value };
      try {
        await updateUser({
          variables: {
            input: {
              firstName: updated.firstName,
              lastName: updated.lastName,
              height: updated.height ? parseFloat(updated.height) : null,
              calorieGoal: updated.calorieGoal
                ? parseInt(updated.calorieGoal, 10)
                : null,
              gender: updated.gender,
              fitnessGoal: updated.fitnessGoal,
              activityLevel: updated.activityLevel,
            },
          },
        });
        await persistWeight(updated.weight);
        reset(updated);
      } catch (error) {
        console.error('Failed to update profile:', error);
      }
    },
    [setValue, getValues, updateUser, reset, persistWeight],
  );

  if (!currentUser) return null;

  return (
    <FullHeightWrapper>
      <ScrollView
        className="flex-1"
        contentContainerClassName="p-6 xl:p-8"
        showsVerticalScrollIndicator={false}
      >
        {/* Two-column layout on desktop */}
        <View className="flex flex-col xl:flex-row gap-6 xl:gap-8 w-full">
          {/* Left column */}
          <View className="flex flex-col gap-6 xl:flex-1">
            {/* Profile Avatar Card */}
            <View className="xl:bg-surface xl:rounded-2xl xl:p-6">
              <ProfileAvatarSection
                firstName={currentUser.firstName}
                lastName={currentUser.lastName}
                email={currentUser.email}
                avatarUrl={currentUser.avatarUrl}
                createdAt={currentUser.createdAt}
              />
            </View>

            {/* Personal Information Card */}
            <View className="bg-surface rounded-2xl p-4 xl:p-6 gap-4 xl:gap-5">
              <Text className="text-sm xl:text-base font-semibold">
                {t('settings.personalInfo.title')}
              </Text>

              <View className="flex flex-row gap-3 xl:gap-4">
                <View className="flex-1 gap-2">
                  <Text className="text-xs xl:text-[13px] font-medium text-text-secondary">
                    {t('settings.profile.firstName')}
                  </Text>
                  <Controller
                    control={control}
                    name="firstName"
                    rules={{
                      required: t('settings.validation.firstNameRequired'),
                    }}
                    render={({ field: { onChange, onBlur, value } }) => (
                      <View>
                        <Input
                          value={value}
                          onChangeText={onChange}
                          onBlur={onBlur}
                          placeholder={t('settings.profile.firstName')}
                          className="bg-surface-elevated rounded-lg"
                        />
                        {errors.firstName && (
                          <Text className="text-destructive text-xs mt-1">
                            {errors.firstName.message}
                          </Text>
                        )}
                      </View>
                    )}
                  />
                </View>

                <View className="flex-1 gap-2">
                  <Text className="text-xs xl:text-[13px] font-medium text-text-secondary">
                    {t('settings.profile.lastName')}
                  </Text>
                  <Controller
                    control={control}
                    name="lastName"
                    rules={{
                      required: t('settings.validation.lastNameRequired'),
                    }}
                    render={({ field: { onChange, onBlur, value } }) => (
                      <View>
                        <Input
                          value={value}
                          onChangeText={onChange}
                          onBlur={onBlur}
                          placeholder={t('settings.profile.lastName')}
                          className="bg-surface-elevated rounded-lg"
                        />
                        {errors.lastName && (
                          <Text className="text-destructive text-xs mt-1">
                            {errors.lastName.message}
                          </Text>
                        )}
                      </View>
                    )}
                  />
                </View>
              </View>

              {/* Email (desktop only). Conditionally rendered (not `display:none`
                  via `hidden xl:flex`) to avoid the iOS New Arch
                  phone-`display:none` re-render crash (RN #52349). */}
              {isDesktop && (
                <View className="gap-2">
                  <Text className="text-[13px] font-medium text-text-secondary">
                    {t('settings.profile.email')}
                  </Text>
                  <Input
                    value={currentUser.email}
                    editable={false}
                    className="bg-surface-elevated rounded-lg"
                  />
                </View>
              )}

              {/* Save button for text fields */}
              {isDirty && (
                <Button
                  onPress={handleSubmit(onSubmit)}
                  loading={updating}
                  className="xl:self-start"
                >
                  <Text>{t('settings.profile.save')}</Text>
                </Button>
              )}
            </View>
          </View>

          {/* Right column */}
          <View className="flex flex-col gap-6 xl:flex-1">
            {/* Body Metrics Card */}
            <View className="bg-surface rounded-2xl p-4 xl:p-6 gap-4 xl:gap-5">
              <View className="flex-row items-center justify-between">
                <Text className="text-sm xl:text-base font-semibold">
                  {t('settings.bodyMetrics.title')}
                </Text>
                {latestMeasurement?.source && (
                  <MeasurementSourceBadge
                    source={latestMeasurement.source}
                    testID="body-metrics-source-badge"
                  />
                )}
              </View>

              <View className="flex flex-row gap-3 xl:gap-4">
                <View className="flex-1 gap-2">
                  <Text className="text-xs xl:text-[13px] font-medium text-text-secondary">
                    {t('settings.bodyMetrics.weight')}
                  </Text>
                  <Controller
                    control={control}
                    name="weight"
                    render={({ field: { onChange, onBlur, value } }) => (
                      <View className="flex-row items-center bg-surface-elevated rounded-lg">
                        <Input
                          value={value}
                          onChangeText={onChange}
                          onBlur={onBlur}
                          keyboardType="decimal-pad"
                          placeholder="0"
                          className="flex-1 bg-transparent"
                        />
                        <Text className="text-xs text-text-secondary pr-3">
                          {t('settings.bodyMetrics.weightUnit')}
                        </Text>
                      </View>
                    )}
                  />
                </View>

                <View className="flex-1 gap-2">
                  <Text className="text-xs xl:text-[13px] font-medium text-text-secondary">
                    {t('settings.bodyMetrics.height')}
                  </Text>
                  <Controller
                    control={control}
                    name="height"
                    render={({ field: { onChange, onBlur, value } }) => (
                      <View className="flex-row items-center bg-surface-elevated rounded-lg">
                        <Input
                          value={value}
                          onChangeText={onChange}
                          onBlur={onBlur}
                          keyboardType="decimal-pad"
                          placeholder="0"
                          className="flex-1 bg-transparent"
                        />
                        <Text className="text-xs text-text-secondary pr-3">
                          {t('settings.bodyMetrics.heightUnit')}
                        </Text>
                      </View>
                    )}
                  />
                </View>
              </View>

              <View className="gap-2">
                <Text className="text-xs xl:text-[13px] font-medium text-text-secondary">
                  {t('settings.bodyMetrics.calorieGoal')}
                </Text>
                <Controller
                  control={control}
                  name="calorieGoal"
                  render={({ field: { onChange, onBlur, value } }) => {
                    // A configurable calorie/macro target is a Pro feature. A free
                    // user sees the SAME editable-looking field, but focusing it opens
                    // the paywall and blurs immediately — the value is never edited or
                    // saved (onChange is not wired), so no mutation fires.
                    const isFree = !canMacroTargets;
                    return (
                      <View className="flex-row items-center bg-surface-elevated rounded-lg">
                        <Input
                          testID="macro-target-input"
                          value={value}
                          onChangeText={isFree ? undefined : onChange}
                          onBlur={isFree ? undefined : onBlur}
                          onFocus={
                            isFree
                              ? () => {
                                  // Blur back out (drop the keyboard/focus) and upsell;
                                  // opening the paywall must not leave the field editable.
                                  Keyboard.dismiss();
                                  openPaywall();
                                }
                              : undefined
                          }
                          keyboardType="numeric"
                          placeholder="0"
                          className="flex-1 bg-transparent"
                        />
                        <Text className="text-xs text-text-secondary pr-3">
                          {t('settings.bodyMetrics.calorieGoalUnit')}
                        </Text>
                      </View>
                    );
                  }}
                />
              </View>

              <View className="gap-2">
                <Text className="text-xs xl:text-[13px] font-medium text-text-secondary">
                  {t('settings.bodyMetrics.gender')}
                </Text>
                <Controller
                  control={control}
                  name="gender"
                  render={({ field: { value } }) => (
                    <GenderSelector
                      selected={value}
                      onSelect={(g) => handleSelectionChange('gender', g)}
                    />
                  )}
                />
              </View>

              {/* Latest composition summary (read-only). Meets the AC that fat %,
                  muscle and BMI show up without navigating; BMI is derived. */}
              <View className="gap-2">
                <Text className="text-xs xl:text-[13px] font-medium text-text-secondary">
                  {t('settings.bodyMetrics.latestComposition')}
                </Text>
                <View className="flex-row gap-2">
                  {(
                    [
                      [
                        'settings.bodyMetrics.bodyFat',
                        latestMeasurement?.bodyFatPercent != null
                          ? `${latestMeasurement.bodyFatPercent}%`
                          : null,
                      ],
                      [
                        'settings.bodyMetrics.muscle',
                        latestMeasurement?.leanMass != null
                          ? `${latestMeasurement.leanMass} ${t('settings.bodyMetrics.weightUnit')}`
                          : null,
                      ],
                      [
                        'settings.bodyMetrics.bmi',
                        BmiCalculator.compute(
                          latestMeasurement?.weight,
                          currentUser.height,
                        ),
                      ],
                    ] as const
                  ).map(([labelKey, value]) => (
                    <View
                      key={labelKey}
                      className="flex-1 gap-0.5 rounded-lg bg-surface-elevated p-3"
                    >
                      <Text className="text-xs text-text-muted">
                        {t(labelKey)}
                      </Text>
                      <Text className="text-sm font-semibold">
                        {value != null
                          ? String(value)
                          : t('settings.bodyMetrics.notAvailable')}
                      </Text>
                    </View>
                  ))}
                </View>
              </View>

              {/* Entry to the full history/trend screen. */}
              <Button
                variant="secondary"
                onPress={() => router.push('/profile/body-composition')}
                className="flex-row justify-between"
                testID="view-body-composition-trend"
                collapsable={false}
              >
                <View className="flex-row items-center gap-x-2">
                  <Icon name="ChartColumn" size={18} className="text-accent" />
                  <Text>{t('settings.bodyMetrics.viewTrend')}</Text>
                </View>
                <Icon name="ChevronRight" size={18} />
              </Button>
            </View>

            {/* Connected data / integrations */}
            <HealthConnectCard />

            {/* Fitness Goal Card */}
            <View className="bg-surface rounded-2xl p-4 xl:p-6 gap-4">
              <Text className="text-sm xl:text-base font-semibold">
                {t('settings.fitnessGoal.title')}
              </Text>
              <Controller
                control={control}
                name="fitnessGoal"
                render={({ field: { value } }) => (
                  <FitnessGoalSelector
                    selected={value}
                    onSelect={(g) => handleSelectionChange('fitnessGoal', g)}
                  />
                )}
              />
            </View>

            {/* Activity Level Card */}
            <View className="bg-surface rounded-2xl p-4 xl:p-6 gap-4">
              <Text className="text-sm xl:text-base font-semibold">
                {t('settings.activityLevel.title')}
              </Text>
              <Controller
                control={control}
                name="activityLevel"
                render={({ field: { value } }) => (
                  <ActivityLevelSelector
                    selected={value}
                    onSelect={(l) => handleSelectionChange('activityLevel', l)}
                  />
                )}
              />
            </View>

            {/* Subscription */}
            <Button
              variant="secondary"
              onPress={() => router.push('/subscription')}
              className="flex-row gap-x-2"
            >
              <Icon name="CreditCard" size={18} />
              <Text>{t('subscription:manage')}</Text>
            </Button>

            {/* Danger Zone */}
            <DangerZoneCard />
          </View>
        </View>
      </ScrollView>
    </FullHeightWrapper>
  );
};
